import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, ShoppingCart, Heart } from 'lucide-react';
import { useMarketplace } from '../../context/MarketplaceContext';
import { getMerchantById } from '../../data/marketplaceData';

export default function ProductCard({ product, compact = false }) {
  const navigate = useNavigate();
  const { addToCart, trackProductView } = useMarketplace();
  const [isAdding, setIsAdding] = useState(false);
  const [imageError, setImageError] = useState(false);

  const merchant = getMerchantById(product.merchantId);

  const handleClick = () => {
    trackProductView(product.id);
    // Navigate to dedicated product detail page
    navigate(`/marketplace/${product.id}`);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    setIsAdding(true);
    addToCart(product.id, 1);
    setTimeout(() => setIsAdding(false), 600);
  };

  if (compact) {
    return (
      <button
        onClick={handleClick}
        className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 transition-all active:scale-[0.98] cursor-pointer text-left hover:shadow-md hover:border-indigo-200 flex gap-3 p-3"
      >
        {/* Image */}
        <div className="relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
          {!imageError ? (
            <img
              src={product.images[0]}
              alt={product.name}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 text-2xl font-bold">
              {product.name.charAt(0)}
            </div>
          )}
          {product.discount > 0 && (
            <div className="absolute top-1 left-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
              -{product.discount}%
            </div>
          )}
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-gray-900 text-sm line-clamp-2 leading-tight">
            {product.name}
          </h4>
          <div className="flex items-center gap-1 mt-1">
            <Star size={12} className="text-amber-400 fill-amber-400" />
            <span className="text-xs text-gray-600">{product.rating}</span>
            <span className="text-xs text-gray-400">({product.reviewCount})</span>
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="font-black text-gray-900">${product.price.toFixed(2)}</span>
            {product.originalPrice > product.price && (
              <span className="text-xs text-gray-400 line-through">${product.originalPrice.toFixed(2)}</span>
            )}
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 transition-all hover:shadow-md hover:border-indigo-200 group">
      {/* Image */}
      <button
        onClick={handleClick}
        className="relative w-full aspect-square bg-gray-100 overflow-hidden"
      >
        {!imageError ? (
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-4xl font-bold">
            {product.name.charAt(0)}
          </div>
        )}

        {/* Discount Badge */}
        {product.discount > 0 && (
          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow">
            -{product.discount}%
          </div>
        )}

        {/* Featured Badge */}
        {product.featured && (
          <div className="absolute top-2 right-2 bg-indigo-600 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow">
            Featured
          </div>
        )}

        {/* Quick Add Button */}
        <button
          onClick={handleAddToCart}
          className={`absolute bottom-2 right-2 p-2.5 rounded-full shadow-lg transition-all ${
            isAdding
              ? 'bg-green-500 text-white scale-110'
              : 'bg-white text-indigo-600 hover:bg-indigo-600 hover:text-white'
          }`}
        >
          <ShoppingCart size={18} className={isAdding ? 'animate-bounce' : ''} />
        </button>
      </button>

      {/* Details */}
      <button
        onClick={handleClick}
        className="p-4 text-left w-full"
      >
        {/* Merchant */}
        <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wide mb-1">
          {merchant?.name}
        </p>

        {/* Product Name */}
        <h4 className="font-bold text-gray-900 text-sm line-clamp-2 leading-tight min-h-[2.5rem]">
          {product.name}
        </h4>

        {/* Rating */}
        <div className="flex items-center gap-1 mt-2">
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                size={12}
                className={star <= Math.round(product.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}
              />
            ))}
          </div>
          <span className="text-xs text-gray-500">({product.reviewCount})</span>
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-2 mt-2">
          <span className="font-black text-lg text-gray-900">${product.price.toFixed(2)}</span>
          {product.originalPrice > product.price && (
            <span className="text-sm text-gray-400 line-through">${product.originalPrice.toFixed(2)}</span>
          )}
        </div>

        {/* Sales indicator */}
        {product.sales >= 500 && (
          <p className="text-[10px] text-emerald-600 font-bold mt-1">
            {product.sales.toLocaleString()}+ sold
          </p>
        )}
      </button>
    </div>
  );
}
